import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  type AnyRoute,
} from '@tanstack/react-router';
import { PageLoading } from '@shared/ui/page-loading';
import { NotFoundPage, RootRoute } from './placeholders';

// ----------------------------------------------------------------------------
// Page chunks are lazy-loaded so the initial bundle stays small.
// `lazyRouteComponent(loader, exportName)` is TanStack Router's native helper.
// ----------------------------------------------------------------------------

const rootRoute = createRootRoute({
  component: RootRoute,
  notFoundComponent: NotFoundPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@features/home'), 'HomePage'),
  pendingComponent: PageLoading,
});

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  component: lazyRouteComponent(() => import('@features/profile'), 'ProfilePage'),
  pendingComponent: PageLoading,
});

const exercisesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/exercises',
  component: lazyRouteComponent(() => import('@features/exercises'), 'ExercisesPage'),
  pendingComponent: PageLoading,
});

const exerciseDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/exercises/$exerciseId',
  component: lazyRouteComponent(() => import('@features/exercises'), 'ExerciseDetailPage'),
  pendingComponent: PageLoading,
});

const programsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/programs',
  component: lazyRouteComponent(() => import('@features/programs'), 'ProgramsPage'),
  pendingComponent: PageLoading,
});

const programEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/programs/$programId',
  component: lazyRouteComponent(() => import('@features/programs'), 'ProgramEditorPage'),
  pendingComponent: PageLoading,
});

const startWorkoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/workout/new',
  // Deep import: the chooser page is small (~10 KB) but the barrel pulls
  // GuidedWorkoutSession + AddExerciseToWorkoutDialog with it (~1 MB). Split
  // so /workout/new doesn't drag the whole session machinery on first load.
  component: lazyRouteComponent(
    () => import('@features/workouts/presentation/StartWorkoutPage'),
    'StartWorkoutPage',
  ),
  pendingComponent: PageLoading,
});

const workoutSessionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/workout/$sessionId',
  // Same deep-import treatment: session page owns the heaviest UI (guided
  // flow, stats, add-exercise dialog). Lives in its own chunk now.
  component: lazyRouteComponent(
    () => import('@features/workouts/presentation/WorkoutSessionPage'),
    'WorkoutSessionPage',
  ),
  pendingComponent: PageLoading,
});

const progressRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/progress',
  component: lazyRouteComponent(() => import('@features/progress'), 'ProgressPage'),
  pendingComponent: PageLoading,
});

const inviteRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/i/$code',
  component: lazyRouteComponent(() => import('@features/relationships'), 'InviteRedirectPage'),
  pendingComponent: PageLoading,
});

const coachRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/coach',
  component: lazyRouteComponent(() => import('@features/relationships'), 'CoachPage'),
  pendingComponent: PageLoading,
});

const athleteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/coach/$athleteId',
  component: lazyRouteComponent(() => import('@features/relationships'), 'AthletePage'),
  pendingComponent: PageLoading,
});

const friendRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/u/$profileId',
  component: lazyRouteComponent(() => import('@features/relationships'), 'FriendPage'),
  pendingComponent: PageLoading,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  profileRoute,
  programsRoute,
  programEditorRoute,
  startWorkoutRoute,
  workoutSessionRoute,
  progressRoute,
  exercisesRoute,
  exerciseDetailRoute,
  inviteRedirectRoute,
  coachRoute,
  athleteRoute,
  friendRoute,
] as AnyRoute[]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingComponent: PageLoading,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
